import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import * as Icon from "react-bootstrap-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { SuccessOverlay, InlineAlert } from "./ui/Toast";
import ImicoSignaturePad from "./ImicoSignaturePad";

// ────────────────────────────────────────────────
// Government-style theme
// ────────────────────────────────────────────────
const cyberTheme = {
  card: "#ffffff",
  text: "#1a2332",
  muted: "#4a5568",
  primary: "#003580",
  accent: "#c8a84b",
  glow: "0 4px 24px rgba(0,53,128,0.12)",
  borderGlow: "1px solid #dde3ea",
  inputBg: "#f8fafc",
  buttonGradient: "linear-gradient(135deg, #003580, #1a56b0)",
};

const RegisterB = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    izina_ry_umudugudu: "",
    umujyihe: "",
    national_id: "",
    fullname: "",
    email: "",
    telefone: "",
    password: "",
    confirmPassword: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Signature captured at registration time — auto-stamped onto this
  // leader's future reports via GET /signature/my-signature, same lookup
  // the report flow already uses.
  const [signatureImage, setSignatureImage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setShowForm(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    const { izina_ry_umudugudu, national_id, fullname, email, telefone, password, confirmPassword } = formData;

    if (!izina_ry_umudugudu) newErrors.izina_ry_umudugudu = "Izina ry'Umudugudu rirakenewe";
    if (!national_id || national_id.length !== 16) newErrors.national_id = "Imibare 16 irakenewe";
    if (!fullname) newErrors.fullname = "Amazina arasabwa";
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) newErrors.email = "Email ntiyemewe";
    if (!telefone || !/^07\d{8}$/.test(telefone)) newErrors.telefone = "Telefone igomba kuba 07xxxxxxxx";
    if (!password || password.length < 6) newErrors.password = "Nibura inyuguti 6";
    if (password !== confirmPassword) newErrors.confirmPassword = "Amagambo y'ibanga ntahura";
    if (!signatureImage) newErrors.signature = "Umukono urakenewe";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const token = localStorage.getItem("token");
      let sup_id = null;
      if (token) {
        try { sup_id = JSON.parse(atob(token.split(".")[1])).id; } catch (_) {}
      }
      if (!sup_id) {
        setErrorMessage("Ntabwo winjiye. Banza winjire (Login).");
        setIsLoading(false);
        return;
      }
      await axios.post("/register_mudugudu_public", { ...formData, sup_id, signature_image: signatureImage });
      setSuccessMessage(true);
      setTimeout(() => {
        setSuccessMessage(false);
        if (onSuccess) onSuccess();
        else navigate("/");
      }, 4000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Ikosa mu kwiyandikisha");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center w-100 py-4">
      <style>{`
        .glass-card {
          background: ${cyberTheme.card};
          border: ${cyberTheme.borderGlow};
          border-radius: 20px;
          box-shadow: ${cyberTheme.glow};
          padding: 2.5rem;
          max-width: 850px;
          width: 100%;
          position: relative;
          overflow: hidden;
        }

        .glass-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #003580, #c8a84b, #003580);
        }

        .cyber-title {
          font-size: 2rem;
          font-weight: 800;
          color: #003580;
          letter-spacing: -0.5px;
          margin-bottom: 0.5rem;
        }

        .input-cyber {
          background: ${cyberTheme.inputBg} !important;
          border: 1px solid #dde3ea !important;
          border-radius: 10px !important;
          color: #1a2332 !important;
          height: 56px !important;
          padding-left: 3.2rem !important;
          font-size: 1rem !important;
          transition: all 0.2s ease !important;
          /* Without this, browsers with OS/browser dark mode enabled
             auto-darken native form controls and render typed text in a
             near-invisible light gray meant for a dark input, even though
             this input is explicitly light-themed. */
          color-scheme: light;
        }

        .input-cyber:focus {
          border-color: #003580 !important;
          box-shadow: 0 0 0 3px rgba(0,53,128,0.1) !important;
          background: #fff !important;
        }

        .cyber-icon {
          position: absolute;
          left: 1.1rem;
          top: 28px;
          transform: translateY(-50%);
          color: #003580;
          font-size: 1.2rem;
          z-index: 10;
        }

        .form-label {
          color: #1a2332 !important;
          font-weight: 600;
          margin-bottom: 0.4rem;
          display: block;
          font-size: 0.9rem;
        }

        .btn-cyber {
          background: ${cyberTheme.buttonGradient};
          border: none;
          color: white;
          font-weight: 700;
          font-size: 1.05rem;
          height: 56px;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0,53,128,0.25);
          transition: all 0.3s ease;
        }

        .btn-cyber:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,53,128,0.35);
          background: linear-gradient(135deg, #002a6b, #003580);
        }

        .error-cyber {
          color: #b91c1c;
          font-size: 0.82rem;
          margin-top: 4px;
        }

        @media (max-width: 575px) {
          .glass-card { padding: 1.5rem 1.25rem; border-radius: 16px; }
          .cyber-title { font-size: 1.5rem; }
          .input-cyber { height: 50px !important; font-size: 16px !important; }
          .btn-cyber { height: 50px; font-size: 1rem; }
        }

        /* ── Success Message Overlay ── */
        .success-overlay {
          position: fixed;
          top: 0; left: 0;
          width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 1rem;
        }

        .success-card {
          background: #ffffff;
          border: 2px solid rgba(0,53,128,0.2);
          border-radius: 20px;
          padding: 2.5rem 3rem;
          text-align: center;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0,53,128,0.2);
          position: relative;
          overflow: hidden;
        }

        .success-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #003580, #c8a84b);
        }

        .success-card > * { position: relative; z-index: 2; }

        .success-icon-ring {
          width: 90px; height: 90px;
          border-radius: 50%;
          background: rgba(26,122,74,0.1);
          border: 3px solid rgba(26,122,74,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.25rem;
          box-shadow: 0 0 20px rgba(26,122,74,0.15);
        }

        .success-icon-ring svg { color: #1a7a4a; }

        .success-title {
          font-size: 1.6rem;
          font-weight: 800;
          color: #003580;
          margin-bottom: 0.6rem;
        }

        .success-subtitle {
          color: #4a5568;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 0.5rem;
        }

        .success-name {
          color: #003580;
          font-weight: 700;
          font-size: 1.05rem;
        }

        .success-bar {
          width: 100%; height: 4px;
          background: #f0f4f8;
          border-radius: 4px;
          margin-top: 1.5rem;
          overflow: hidden;
        }

        .success-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #003580, #c8a84b);
          border-radius: 4px;
          animation: barFill 4s linear forwards;
        }

        @keyframes barFill { from { width: 100%; } to { width: 0%; } }

        .success-redirect {
          color: #718096;
          font-size: 0.82rem;
          margin-top: 0.75rem;
        }

        .sparkle {
          position: absolute;
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #c8a84b;
          animation: sparkleFloat 2s ease-in-out infinite;
        }

        .sparkle:nth-child(1) { top: 12%; left: 8%; animation-delay: 0s; }
        .sparkle:nth-child(2) { top: 18%; right: 12%; animation-delay: 0.5s; background: #003580; }
        .sparkle:nth-child(3) { bottom: 22%; left: 18%; animation-delay: 1s; }
        .sparkle:nth-child(4) { bottom: 12%; right: 8%; animation-delay: 1.5s; background: #1a7a4a; }

        @keyframes sparkleFloat {
          0%, 100% { opacity: 0; transform: scale(0) translateY(0); }
          50% { opacity: 1; transform: scale(1) translateY(-8px); }
        }

        /* ── Error Toast ── */
        .error-toast {
          background: rgba(185,28,28,0.06);
          border: 1px solid rgba(185,28,28,0.25);
          border-radius: 12px;
          padding: 0.9rem 1.25rem;
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: errorShake 0.4s ease-in-out;
        }

        @keyframes errorShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }

        .error-toast-icon { color: #b91c1c; font-size: 1.2rem; flex-shrink: 0; }
        .error-toast-text { color: #7f1d1d; font-size: 0.9rem; font-weight: 500; }
        .error-toast-close {
          background: none; border: none; color: #b91c1c;
          font-size: 1.1rem; cursor: pointer; margin-left: auto;
          opacity: 0.7; transition: opacity 0.2s;
        }
        .error-toast-close:hover { opacity: 1; }

        /* ── Signature pad (shared look with ImicoSignaturePad) ── */
        .imico-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border:none; border-radius:7px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; }
        .imico-btn:disabled { opacity:.5; cursor:not-allowed; }
        .imico-btn-primary { background:#003580; color:#fff; }
        .imico-btn-primary:hover:not(:disabled) { background:#002a6b; }
        .imico-btn-outline { background:transparent; color:#475569; border:1px solid #d1d9e6; }
        .imico-btn-outline:hover { background:#f1f5f9; }
        .imico-btn-sm { padding:7px 13px; font-size:12px; }
        .imico-sig-pad-wrap { margin-top:10px; }
        .imico-sig-pad-meta { display:flex; justify-content:space-between; font-size:12px; color:#475569; margin-bottom:6px; }
        .imico-sig-pad-label { font-weight:700; }
        .imico-sig-pad { background:#f8fafc; border:2px dashed #cbd5e1; border-radius:8px; padding:10px; }
        .imico-sig-canvas { width:100% !important; height:160px !important; border-radius:6px; background:#fff; border:1px solid #e2e8f0; touch-action:none; }
        .imico-sig-actions { display:flex; gap:10px; margin-top:8px; }
        .imico-alert { padding:10px 14px; border-radius:6px; font-size:12px; margin-bottom:10px; }
        .imico-alert-warning { background:#fef3c7; border:1px solid #fde68a; color:#b45309; }
      `}</style>

      {/* ── Success Overlay ── */}
      <SuccessOverlay
        show={successMessage}
        title="Byagenze Neza!"
        subtitle="Umuyobozi w'umudugudu yanditswe neza muri sisitemu."
        name={formData.fullname}
        onClose={() => {
          setSuccessMessage(false);
          if (onSuccess) onSuccess();
          else navigate("/");
        }}
      />

      <motion.div
        className="glass-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showForm ? 1 : 0, y: showForm ? 0 : 30 }}
      >
        <div className="text-center mb-4">
          <h1 className="cyber-title">Kwiyandikisha</h1>
          <p style={{ color: "#4a5568", fontSize: "0.92rem" }}>Injiza amakuru y'umuyobozi mushya muri sisitemu.</p>
        </div>

        {/* ── Error Alert ── */}
        <InlineAlert
          type="err"
          msg={errorMessage}
          onClose={() => setErrorMessage("")}
        />

        <form onSubmit={handleRegister}>
          <div className="row g-4">
            <div className="col-md-6">
              <label className="form-label">Izina ry'Umudugudu</label>
              <div className="position-relative">
                <i className="bi bi-geo-alt cyber-icon"></i>
                <input
                  name="izina_ry_umudugudu"
                  className="input-cyber form-control"
                  placeholder="Izina ry'umudugudu..."
                  value={formData.izina_ry_umudugudu}
                  onChange={handleChange}
                />
              </div>
              {errors.izina_ry_umudugudu && <div className="error-cyber">{errors.izina_ry_umudugudu}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Umujyihe (Aho Uherereye)</label>
              <div className="position-relative">
                <i className="bi bi-map cyber-icon"></i>
                <input
                  name="umujyihe"
                  className="input-cyber form-control"
                  placeholder="Akagari, Umurenge, Akarere..."
                  value={formData.umujyihe}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Indangamuntu</label>
              <div className="position-relative">
                <i className="bi bi-fingerprint cyber-icon"></i>
                <input
                  name="national_id"
                  className="input-cyber form-control"
                  placeholder="1 1990..."
                  value={formData.national_id}
                  onChange={(e) => setFormData({...formData, national_id: e.target.value.replace(/\D/g, "").slice(0, 16)})}
                  maxLength={16}
                />
              </div>
              {errors.national_id && <div className="error-cyber">{errors.national_id}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Amazina Yose</label>
              <div className="position-relative">
                <i className="bi bi-person cyber-icon"></i>
                <input
                  name="fullname"
                  className="input-cyber form-control"
                  placeholder="John Doe"
                  value={formData.fullname}
                  onChange={handleChange}
                />
              </div>
              {errors.fullname && <div className="error-cyber">{errors.fullname}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Email Aderesi</label>
              <div className="position-relative">
                <i className="bi bi-envelope cyber-icon"></i>
                <input
                  name="email"
                  type="email"
                  className="input-cyber form-control"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && <div className="error-cyber">{errors.email}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Telefone</label>
              <div className="position-relative">
                <i className="bi bi-telephone cyber-icon"></i>
                <input
                  name="telefone"
                  className="input-cyber form-control"
                  placeholder="078..."
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value.replace(/\D/g, "").slice(0, 10)})}
                  maxLength={10}
                />
              </div>
              {errors.telefone && <div className="error-cyber">{errors.telefone}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Ijambo ry'ibanga</label>
              <div className="position-relative">
                <i className="bi bi-lock cyber-icon"></i>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="input-cyber form-control"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
              {errors.password && <div className="error-cyber">{errors.password}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Emeza Ijambo ry'ibanga</label>
              <div className="position-relative">
                <i className="bi bi-shield-lock cyber-icon"></i>
                <input
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  className="input-cyber form-control"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
              {errors.confirmPassword && <div className="error-cyber">{errors.confirmPassword}</div>}
            </div>
          </div>

          <div className="mt-3 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              style={{ accentColor: "#003580", width: '18px', height: '18px' }}
              onChange={() => setShowPassword(!showPassword)}
            />
            <span style={{ color: "#1a2332", fontSize: "0.9rem" }}>Erekana amagambo y'ibanga</span>
          </div>

          <div className="mt-4">
            <label className="form-label">Umukono w'Umuyobozi</label>
            {signatureImage ? (
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <img src={signatureImage} alt="Umukono" style={{ height: 70, border: "1px solid #dde3ea", borderRadius: 8, background: "#fff" }} />
                <button type="button" className="imico-btn imico-btn-outline imico-btn-sm" onClick={() => setSignatureImage(null)}>
                  Hindura Umukono
                </button>
              </div>
            ) : (
              <ImicoSignaturePad
                label="Shyiraho umukono wawe"
                signerName={formData.fullname || "Umuyobozi mushya"}
                onSign={(dataUrl) => { setSignatureImage(dataUrl); setErrors((prev) => ({ ...prev, signature: null })); }}
              />
            )}
            {errors.signature && <div className="error-cyber">{errors.signature}</div>}
          </div>

          <button className="btn-cyber w-100 mt-4" disabled={isLoading} type="submit">
            {isLoading ? (
              <span className="spinner-border spinner-border-sm me-2"></span>
            ) : (
              <><Icon.CheckCircleFill className="me-2" /> Emeza Kwiyandikisha</>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterB;